import * as Validators from "src/utils/validators";
import CurrencyConverter from "src/utils/CurrencyConversionService";
import { toUiCollType } from "src/utils/converting";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { getVWAvgRate, getTermFromDate } from "src/utils";
import { CounterPartysSelectors } from "src/redux/CounterPartys/selectors";
import {
    INTERMEDIARY_SELECT_TYPES,
    TX_TYPE_VALUES,
} from "src/redux/Confirmations/constants";

// not as pretty, but very safe
const mapInventoryRow = (entry, index) => {
    let row = {
        id: index,
        LastUpdated: entry.modifiedAt,
        ListName: entry.listName,
        Country: entry.security.country,
        UploadedQuantity: entry.uploadedQuantity,
        PreferredRate: entry.preferredRate,
        Utilization: entry.utilization,
        ValueDate: entry.valueDate,
        Dividend: entry.dividend,
        FR: entry.fr,
        Maturity: entry.maturityDate,
        Term: getTermFromDate(entry.valueDate, entry.maturityDate),
        Callable: entry.callable,
        TruQuantity: entry.truQuantity,
        ConfirmedQuantity: entry.confirmedQuantity,
        MyValue: CurrencyConverter.convertFromCountry(
            entry.security.closingPrice * entry.uploadedQuantity,
            entry.security.country
        ).value(),
        TruValue: CurrencyConverter.convertFromCountry(
            entry.security.closingPrice * entry.truQuantity,
            entry.security.country
        ).value(),
        TxType: entry.txType || TX_TYPE_VALUES.ANY, // UI has no blank option
        Intermediary: entry.intermediary || INTERMEDIARY_SELECT_TYPES.ANY,
    };
    if (entry.security) {
        row.SecurityId = entry.security.id;
        row.ISIN = entry.security.isin;
        row.BbgCode = entry.security.bbgCode;
        row.Ticker = entry.security.ticker;
        row.Name = entry.security.name;
        row.LocalName = entry.security.localName;
        row.PreviousClose = CurrencyConverter.convertFromCountry(
            entry.security.closingPrice,
            entry.security.country
        ).value();
        if (entry.security.tradableDates !== undefined) {
            row.TradeableDates = entry.security.tradableDates.map((str) =>
                str.replace(/-/g, "")
            );
        }
    }
    if (entry.collateralType) {
        const { collType, collCcy } = toUiCollType(entry.collateralType);
        row.CollateralType = collType;
        row.CollateralCCY = collCcy;
    }

    row.AdvertisedTo = entry.availabilityCptyNames;
    row.User = entry.lastUser;
    return row;
};

export const InventorySelectors = {
    GetUploadRows: (state) => {
        const uploadRows = state.Inventory.uploadRows;
        return uploadRows.map((row) => ({
            ...row,
            PreviousClose: CurrencyConverter.convertFromCountry(
                row.security?.closingPrice,
                row.Country
            ).value(),
        }));
    },
    GetUploadMode: (state) => state.Inventory.uploadMode,
    IsUploadDataValid: (state) =>
        state.Inventory.uploadRows.length > 0 &&
        state.Inventory.uploadRows.reduce(
            (isValid, row) => isValid && Validators.isInventoryRowValid(row),
            true
        ),
    GetTodayInventory: (state) => state.Inventory.todayInventory,
    GetSpecificInventory: (state, ticker) =>
        state.Inventory.todayInventory[ticker],
    GetInventoryList: (state) => {
        const availabilityByListName = SettingsSelectors.ListSettings(
            state
        ).reduce(
            (acc, item) => ({
                ...acc,
                [item.name]:
                    item.advertiseAvailabilitySettings?.availabilityCptyIds?.map(
                        (id) => CounterPartysSelectors.Get(state, id)?.name
                    ),
            }),
            {}
        );

        let entries = Object.values(state.Inventory.todayInventory)
            .flatMap((x) => x)
            .map((e) => ({
                ...e,
                availabilityCptyNames: availabilityByListName[e.listName],
            }));

        return entries.map(mapInventoryRow);
    },
    GetEditedRows: (state) => {
        return state.Inventory.editedRows;
    },
    GetEditingRowIDs: (state) => {
        return state.Inventory.editingRowIDs;
    },
    GetError: (state) => state.Inventory.error,
    HasMarkitAccess: (state) => state.Inventory.hasMarkitAccess,
    GetSelectedInventoryStats: (state) => {
        const selectedRows = state.Inventory.selected;
        if (!selectedRows.length) return {};

        const isMultiMarketSelected =
            [...new Set(selectedRows.map((row) => row.Country))].length > 1;
        const { preferredCcy } = SettingsSelectors.UserSettings(state);
        const cannotShow = isMultiMarketSelected && !preferredCcy;

        return {
            count: selectedRows.length,
            total: cannotShow
                ? undefined
                : selectedRows.reduce((total, row) => total + row.MyValue, 0),
            vwavg: cannotShow
                ? undefined
                : getVWAvgRate(
                      selectedRows.map((row) => ({
                          rate: row.PreferredRate,
                          value: row.MyValue,
                      }))
                  ),
        };
    },
    GetInventory: (state, ticker, listName) => {
        return state.Inventory.todayInventory[ticker]?.find(
            (i) => i.listName === listName
        );
    },
};
